import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Button, Checkbox, Input, Password, Title } from "rizzui";
import { useModal } from "@/app/shared/modal-views/use-modal.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  apiKeyFormSchema,
  IApiKeyForm,
} from "@/utilities/validators/api-key.ts";
import { CiCirclePlus } from "react-icons/ci";
import { BsDashCircle } from "react-icons/bs";
import MultiSelect from "@/components/multi-select";
import { useEffect } from "react";
import useApi from "@/hooks/useApi.tsx";
import APIKeyRepository from "@/utilities/repositories/ApiKey.ts";
import toast from "react-hot-toast";
import {
  EPermissionApiKey,
  IApiKey,
  IPermissionOption,
} from "@/utilities/types/ApiKey.ts";
import { PiCopySimple } from "react-icons/pi";
import { useCopyToClipboard } from "usehooks-ts";
import cn from "@/utils/class-names";

export const CreateEditApiKey = ({
  row,
  refetchData,
}: {
  row?: IApiKey;
  refetchData?: () => void;
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<IApiKeyForm>({
    defaultValues: {
      name: row?.name || "",
      permissions: row?.permissions || [],
      allowedIPs: row?.allowedIPs || [],
      noLimitIp: row?.noLimitIp || false,
    },
    resolver: zodResolver(apiKeyFormSchema),
  });
  const { closeModal } = useModal();
  const noLimitIp = watch("noLimitIp");

  const {
    request: createApiKey,
    loading: isLoadingCreate,
    response: responseCreate,
  } = useApi({
    request: APIKeyRepository.postApiKey,
  });

  const { request: updateApiKey, loading: isLoadingUpdate } = useApi({
    request: APIKeyRepository.updateApiKey,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "allowedIPs",
  });

  useEffect(() => {
    if (noLimitIp) {
      replace([]);
      setValue("allowedIPs", []);
    } else {
      if (fields.length === 0) {
        append("", { shouldFocus: false });
      }
    }
  }, [noLimitIp, replace, setValue, append, fields.length]);

  const [_, copyToClipboard] = useCopyToClipboard();

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (row) {
        await updateApiKey(row.id, data);
        toast.success("Update Api key success!");
      } else {
        await createApiKey(data);
        toast.success("Create Api key success!");
      }
      refetchData?.();
      // if (isOpen) {
      //   closeModal();
      // }
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
    }
  });

  const permissionOptions = Object.entries(EPermissionApiKey).map(
    ([label, value]) => ({ label, value })
  );

  function handleCopyToClipboard(value: string) {
    copyToClipboard(value);
    toast.success(<b>{`Copied to clipboard`}</b>);
  }

  if (responseCreate) {
    return (
      <div className="grid gap-6 p-6 @container [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900">
        <Password
          label="Api Key"
          placeholder="Api key"
          inputClassName="text-sm [&_input]:px-2"
          readOnly
          helperText={"Api key will be automatically generated after submit"}
          defaultValue={responseCreate?.data?.apiKey}
          prefix={
            <PiCopySimple
              onClick={(_) => {
                handleCopyToClipboard(responseCreate?.data?.apiKey);
              }}
              className={cn(
                "mr-2 h-5 w-5 text-gray-500 cursor-pointer",
                !responseCreate?.data?.apiKey && "pointer-events-none"
              )}
            />
          }
        />
        <Button
          className="md:w-fit w-full right-0"
          onClick={() => closeModal()}
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="grid gap-6 p-6 @container [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
    >
      <div className="col-span-full flex items-center justify-between">
        <Title as="h4" className="font-semibold">
          {row ? "Edit API Key" : "Create API Key"}
        </Title>
      </div>
      <Input
        label="Name"
        placeholder="Name"
        type={"text"}
        {...register("name")}
        className="[&>label>span]:font-medium col-span-full lg:col-span-1"
        error={errors?.name?.message as string | undefined}
      />
      <div className="col-span-full lg:col-span-1">
        <Controller
          name="permissions"
          control={control}
          render={({ field, fieldState }) => {
            const selectedOptionsForMultiSelect = permissionOptions.filter(
              (option) => (field.value || []).includes(option.value)
            );
            const handleMultiSelectChange = (
              selectedOptionsFromComponent: IPermissionOption[]
            ) => {
              const newValues = selectedOptionsFromComponent.map(
                (option) => option.value
              );
              field.onChange(newValues);
            };
            return (
              <MultiSelect
                label="Permissions"
                placeholder="Select permissions"
                options={permissionOptions}
                value={selectedOptionsForMultiSelect}
                onChange={handleMultiSelectChange}
                error={fieldState.error?.message}
                className="[&>label>span]:font-medium"
              />
            );
          }}
        />
      </div>
      {!noLimitIp && (
        <div className="col-span-full">
          <label className="block mb-1.5 text-sm font-medium text-gray-900">
            Allowed IPs
          </label>
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <Input
                {...register(`allowedIPs.${index}` as const)}
                placeholder={`IP Address ${index + 1}`}
                className="flex-grow [&>label>span]:font-medium"
                error={
                  errors?.allowedIPs && Array.isArray(errors.allowedIPs)
                    ? errors.allowedIPs[index]?.message
                    : undefined
                }
              />
              <div className={"flex items-center gap-2"}>
                {index > 0 && (
                  <div>
                    <BsDashCircle
                      size={24}
                      color={"red"}
                      className={"cursor-pointer"}
                      onClick={() => remove(index)}
                    />
                  </div>
                )}
                <div>
                  <CiCirclePlus
                    size={28}
                    className={"cursor-pointer"}
                    onClick={() => append("")}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="w-fit">
        <Controller
          name="noLimitIp"
          control={control}
          render={({ field }) => (
            <Checkbox
              className="ps-2"
              label="No Limit IP"
              aria-label="Select row"
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="col-span-full flex items-center justify-end gap-4">
        <Button
          variant="outline"
          onClick={closeModal}
          className="md:w-auto w-1/2"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoadingCreate || isLoadingUpdate}
          className="md:w-auto w-1/2"
        >
          Save
        </Button>
      </div>
    </form>
  );
};
